import React, { useEffect, useState } from 'react'
import { Button, Space, Switch, Table, Typography, message, Popconfirm } from 'antd'
import {
  ApiOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import {
  getAdminPolls,
  updateAdminPoll,
  deleteAdminQuestion,
  getAdminSteps,
  deleteAdminStep,
} from '../../../features/poll/api-admin'
import { HStack } from 'src/react-app/ui/Stack'
import PollModal from './components/PollModal'
import StepModal from './components/StepModal'
import QuestionModal from './components/QuestionModal'
import PollExpandedRow from './components/PollExpandedRow'

const { Title, Text } = Typography

export default function AdminQuizPage() {
  const navigate = useNavigate()
  const [quizzes, setQuizzes] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false)
  const [editingQuiz, setEditingQuiz] = useState<any>(null)

  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false)
  const [editingQuestion, setEditingQuestion] = useState<any>(null)
  const [selectedQuizId, setSelectedQuizId] = useState<string | null>(null)
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null)
  const [steps, setSteps] = useState<any[]>([])

  const [isStepModalOpen, setIsStepModalOpen] = useState(false)
  const [editingStep, setEditingStep] = useState<any>(null)

  const loadQuizzes = async () => {
    setLoading(true)
    try {
      const data = await getAdminPolls()
      setQuizzes(data)
    } catch (e: any) {
      message.error(e?.message || 'Не удалось загрузить опрос')
    } finally {
      setLoading(false)
    }
  }

  const loadSteps = async (quizId: string) => {
    try {
      const data = await getAdminSteps(quizId)
      setSteps(data.sort((a: any, b: any) => a.order_index - b.order_index))
    } catch (e: any) {
      message.error(e?.message || 'Не удалось загрузить шаги')
    }
  }

  useEffect(() => {
    loadQuizzes()
  }, [])

  const openQuizModal = (quiz?: any) => {
    setEditingQuiz(quiz || null)
    setIsQuizModalOpen(true)
  }

  const openStepModal = (quizId: string, step?: any) => {
    setSelectedQuizId(quizId)
    setEditingStep(step || null)
    setIsStepModalOpen(true)
  }

  const openQuestionModal = async (quizId: string, question?: any, stepId?: string) => {
    setSelectedQuizId(quizId)
    setEditingQuestion(question ? { ...question } : null)
    setSelectedStepId(stepId || null)
    await loadSteps(quizId)
    setIsQuestionModalOpen(true)
  }

  const handleDeleteStep = async (stepId: string) => {
    try {
      await deleteAdminStep(selectedQuizId!, stepId)
      message.success('Шаг удалён')
      await loadSteps(selectedQuizId!)
      await loadQuizzes()
    } catch (e: any) {
      message.error(e?.message || 'Не удалось удалить шаг')
    }
  }

  const handleDeleteQuestion = async (quizId: string, questionId: string) => {
    try {
      await deleteAdminQuestion(quizId, questionId)
      message.success('Вопрос удалён')
      await loadQuizzes()
    } catch (e: any) {
      message.error(e?.message || 'Не удалось удалить вопрос')
    }
  }

  const columns = [
    {
      title: 'Название',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Активен',
      key: 'status',
      width: 100,
      render: (_: any, quiz: any) => (
        <Switch
          checked={quiz.status === 'active'}
          onChange={async checked => {
            const newStatus = checked ? 'active' : 'archived'
            setQuizzes(prev => prev.map(q => (q.id === quiz.id ? { ...q, status: newStatus } : q)))
            try {
              await updateAdminPoll(quiz.id, { status: newStatus })
            } catch (e: any) {
              setQuizzes(prev =>
                prev.map(q => (q.id === quiz.id ? { ...q, status: quiz.status } : q)),
              )
              message.error(e?.message || 'Не удалось обновить статус')
            }
          }}
        />
      ),
    },
    {
      title: 'Шагов',
      key: 'steps',
      width: 80,
      render: (_: any, quiz: any) => quiz.steps?.length || 0,
    },
    {
      title: 'Вопросов',
      key: 'questions',
      width: 120,
      render: (_: any, quiz: any) => quiz.questions?.length || 0,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 350,
      render: (_: any, quiz: any) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/admin/dealers/polls/${quiz.id}/preview`)}
            title="Предпросмотр"
          />
          <Button
            size="small"
            icon={<ApiOutlined />}
            onClick={() => navigate(`/admin/dealers/polls/${quiz.id}/bindings`)}
            title="Системная привязка"
          />
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openQuizModal(quiz)}
            title="Редактировать"
          />
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => openStepModal(quiz.id)}
            title="Добавить шаг"
          />
          <Popconfirm title="Удалить опрос?" onConfirm={() => {}}>
            <Button size="small" icon={<DeleteOutlined />} danger disabled title="Удалить" />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <>
      <Space direction="vertical" size={24} style={{ width: '100%', margin: '0 auto' }}>
        <HStack justify="between" align="center">
          <Title level={2} style={{ margin: 0 }}>
            Управление опросами
          </Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openQuizModal()}>
            Создать опрос
          </Button>
        </HStack>

        <Table
          loading={loading}
          dataSource={quizzes}
          columns={columns}
          rowKey="id"
          pagination={false}
          expandable={{
            onExpand: (expanded, quiz) => {
              if (expanded) {
                loadSteps(quiz.id)
              }
            },
            expandedRowRender: (quiz: any) => (
              <PollExpandedRow
                quiz={quiz}
                onEditStep={openStepModal}
                onDeleteStep={handleDeleteStep}
                onAddQuestion={(quizId, stepId) => openQuestionModal(quizId, undefined, stepId)}
                onEditQuestion={(quizId, question) => openQuestionModal(quizId, question)}
                onDeleteQuestion={handleDeleteQuestion}
              />
            ),
          }}
        />
      </Space>

      <PollModal
        open={isQuizModalOpen}
        quiz={editingQuiz}
        onCancel={() => setIsQuizModalOpen(false)}
        onOk={() => {
          setIsQuizModalOpen(false)
          loadQuizzes()
        }}
      />

      <StepModal
        open={isStepModalOpen}
        quizId={selectedQuizId!}
        step={editingStep}
        onCancel={() => setIsStepModalOpen(false)}
        onOk={() => {
          setIsStepModalOpen(false)
          loadQuizzes()
        }}
      />

      <QuestionModal
        open={isQuestionModalOpen}
        quizId={selectedQuizId!}
        question={editingQuestion}
        defaultStepId={selectedStepId}
        steps={steps}
        onCancel={() => setIsQuestionModalOpen(false)}
        onOk={() => {
          setIsQuestionModalOpen(false)
          loadQuizzes()
        }}
      />
    </>
  )
}
